import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { usersAPI } from "../../api/client";
import { formatDuration } from "../../utils/formatDuration";
import "../../components/users/UserActivityReportModal.css";

function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function todayStr() {
  return localDateStr(new Date());
}
function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

function fmtCell(minutes) {
  const m = Number(minutes) || 0;
  if (m <= 0) return null;
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  if (h === 0) return `${min}m`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}m`;
}

function shortDateLabel(dateStr) {
  const [, m, d] = dateStr.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return { day: d.replace(/^0/, ""), month: months[Number(m) - 1] };
}

// Returns cell style info based on dayType + hours logged
function resolveCell(att) {
  const { dayType, dayLabel, minutes, stages, isExpectedWorkingDay } = att;
  const present = minutes > 0;

  if (dayType === "WEEKLY_OFF") {
    return { bg: "#f0f4f8", fg: "#94a3b8", topLine: "—", bottomLine: null, dim: true };
  }
  if (dayType === "HOLIDAY") {
    return {
      bg: "#ede9fe", fg: "#6d28d9",
      topLine: present ? fmtCell(minutes) : "Holiday",
      bottomLine: dayLabel || null,
      dim: !present,
    };
  }
  if (dayType === "OFF") {
    return {
      bg: "#fef9c3", fg: "#92400e",
      topLine: present ? fmtCell(minutes) : "OFF",
      bottomLine: dayLabel && dayLabel !== "OFF" ? dayLabel : (present && stages ? `${stages} stg` : null),
      dim: !present,
    };
  }
  if (dayType === "HALF_DAY") {
    if (present) {
      const hrs = minutes / 60;
      return { bg: hrs >= 3 ? "#fce7f3" : "#fdf4ff", fg: "#9d174d", topLine: fmtCell(minutes), bottomLine: stages > 0 ? `${stages} stg` : "½ Day", dim: false };
    }
    return { bg: "#fdf4ff", fg: "#c084fc", topLine: "½ Day", bottomLine: dayLabel || null, dim: true };
  }

  // WORKING day
  if (present) {
    const hrs = minutes / 60;
    if (hrs >= 6) return { bg: "#dcfce7", fg: "#15803d", topLine: fmtCell(minutes), bottomLine: stages > 0 ? `${stages} stg` : null, dim: false };
    if (hrs >= 3) return { bg: "#bfdbfe", fg: "#1d4ed8", topLine: fmtCell(minutes), bottomLine: stages > 0 ? `${stages} stg` : null, dim: false };
    return { bg: "#dbeafe", fg: "#2563eb", topLine: fmtCell(minutes), bottomLine: stages > 0 ? `${stages} stg` : null, dim: false };
  }
  // No hours logged — neutral dash for all working days (shift or org-default)
  return { bg: null, fg: "#d1d5db", topLine: "—", bottomLine: null, dim: true };
}

const LEGEND_ITEMS = [
  { bg: "#dbeafe", label: "< 3h logged" },
  { bg: "#bfdbfe", label: "3–6h logged" },
  { bg: "#dcfce7", label: "6h+ logged" },
  { bg: "#fdf4ff", label: "Half Day" },
  { bg: "#fef9c3", label: "Day Off / Leave" },
  { bg: "#ede9fe", label: "Holiday" },
  { bg: "#f0f4f8", label: "Weekly Off" },
];

export default function TeamAttendanceReportPage() {
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");

  const [from, setFrom] = useState(daysAgoStr(29));
  const [to, setTo] = useState(todayStr());

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api
      .get("/users")
      .then((resp) => {
        const users = resp.data.users || [];
        setAllUsers(users);
        setSelectedIds(new Set(users.map((u) => u.id)));
      })
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, []);

  const filteredUsers = allUsers.filter((u) => {
    const q = search.toLowerCase();
    return (
      !q ||
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
  });

  const allSelected = allUsers.length > 0 && selectedIds.size === allUsers.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allUsers.map((u) => u.id)));
  };
  const toggleUser = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const setPreset = (days) => {
    setFrom(daysAgoStr(days - 1));
    setTo(todayStr());
  };

  const loadReport = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setReportLoading(true);
    setReportError("");
    try {
      const resp = await usersAPI.getTeamAttendanceReport(Array.from(selectedIds), from, to);
      setReport(resp.data);
    } catch (err) {
      setReportError(err?.response?.data?.message || "Failed to load report");
    } finally {
      setReportLoading(false);
    }
  }, [selectedIds, from, to]);

  const handleDownload = async () => {
    if (selectedIds.size === 0) return;
    setDownloading(true);
    try {
      const resp = await usersAPI.downloadTeamAttendanceReport(Array.from(selectedIds), from, to);
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-report-${from}-to-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setReportError("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const { dates = [], users: reportUsers = [] } = report || {};

  // Per-date totals (only count logged working days)
  const dateTotals = {};
  for (const date of dates) dateTotals[date] = { minutes: 0, present: 0 };
  for (const u of reportUsers) {
    for (const date of dates) {
      const att = u.attendance[date];
      if (att?.minutes > 0) {
        dateTotals[date].minutes += att.minutes;
        dateTotals[date].present++;
      }
    }
  }

  return (
    <div style={{ padding: "16px 24px", maxWidth: "100%", boxSizing: "border-box" }}>
      <Link className="back-link" to="/admin/users">
        ← Back to User Management
      </Link>

      {/* ── Page header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginTop: 16,
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
            Consolidated Activity Report
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
            Hours per user per day · roster-aware (weekly offs, leaves &amp; holidays shown)
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div className="uar-presets">
            {[7, 14, 30].map((d) => (
              <button key={d} className="uar-preset-btn" onClick={() => setPreset(d)}>
                {d}d
              </button>
            ))}
          </div>
          <div className="uar-date-range">
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            <span className="uar-date-sep">→</span>
            <input type="date" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button
            className="um-btn um-btn-primary uar-download-btn"
            onClick={loadReport}
            disabled={reportLoading || selectedIds.size === 0}
          >
            {reportLoading ? "Loading…" : "Generate"}
          </button>
          <button
            className="um-btn um-btn-secondary uar-download-btn"
            onClick={handleDownload}
            disabled={downloading || !report || selectedIds.size === 0}
          >
            {downloading ? "Downloading…" : "↓ Download PDF"}
          </button>
        </div>
      </div>

      {/* ── User selection panel ── */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 16,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>Select Users</span>
            <span
              style={{
                fontSize: 12, color: "#6b7280", background: "#f3f4f6",
                borderRadius: 12, padding: "1px 8px",
              }}
            >
              {selectedIds.size} of {allUsers.length} selected
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="um-input"
              style={{ width: 180, fontSize: 12, padding: "5px 10px" }}
            />
            <button
              type="button"
              className="uar-toggle-btn uar-toggle-btn--active"
              style={{ fontSize: 12, padding: "4px 12px" }}
              onClick={toggleAll}
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
        </div>

        {usersLoading ? (
          <div className="uar-loading" style={{ padding: "12px 0" }}>
            <div className="uar-spinner" /> Loading users…
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 6,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {filteredUsers.map((u) => {
              const checked = selectedIds.has(u.id);
              return (
                <label
                  key={u.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    padding: "6px 10px", borderRadius: 7, border: "1px solid",
                    borderColor: checked ? "#bfdbfe" : "#e5e7eb",
                    background: checked ? "#eff6ff" : "#f9fafb",
                    fontSize: 13, userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox" checked={checked}
                    onChange={() => toggleUser(u.id)}
                    style={{ accentColor: "#2563eb" }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {u.firstName} {u.lastName}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {u.email}
                    </div>
                  </div>
                </label>
              );
            })}
            {filteredUsers.length === 0 && (
              <div style={{ color: "#9ca3af", fontSize: 13, padding: 8 }}>No users match your search.</div>
            )}
          </div>
        )}
      </div>

      {reportError && <div className="um-error" style={{ marginBottom: 16 }}>{reportError}</div>}

      {/* ── Attendance grid ── */}
      {report && (
        <div>
          {/* Summary stat cards */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            {[
              { label: "Users", value: reportUsers.length, color: "#1d4ed8", bg: "#dbeafe" },
              { label: "Days in Range", value: dates.length, color: "#7c3aed", bg: "#ede9fe" },
              {
                label: "Total Hours Logged",
                value: formatDuration(reportUsers.reduce((s, u) => s + u.summary.totalMinutes, 0)),
                color: "#065f46", bg: "#d1fae5",
              },
              {
                label: "Total Stages",
                value: reportUsers.reduce((s, u) => s + u.summary.totalStages, 0),
                color: "#92400e", bg: "#fef3c7",
              },
            ].map(({ label, value, color, bg }) => (
              <div
                key={label}
                style={{ background: bg, border: `1px solid ${color}22`, borderRadius: 10, padding: "10px 18px", minWidth: 140 }}
              >
                <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, fontSize: 11, color: "#6b7280", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600 }}>Legend:</span>
            {LEGEND_ITEMS.map(({ bg, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 13, height: 13, borderRadius: 3, background: bg, border: "1px solid #d1d5db" }} />
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Scrollable grid — page scrolls to here, then grid fills remaining viewport height */}
          <div
            style={{
              overflowX: "auto",
              overflowY: "auto",
              height: "calc(100vh - 60px - 360px)",
              minHeight: 300,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: "max-content" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky", top: 0, left: 0, zIndex: 4,
                      background: "#1e3a5f", color: "#fff", fontWeight: 700,
                      padding: "10px 14px", textAlign: "left", minWidth: 170,
                      borderRight: "2px solid #334d7a", fontSize: 11,
                    }}
                  >
                    Team Member
                  </th>

                  {dates.map((date) => {
                    const { day, month } = shortDateLabel(date);
                    const dow = new Date(`${date}T00:00:00`).getDay();
                    const isWknd = dow === 0 || dow === 6;
                    return (
                      <th
                        key={date}
                        style={{
                          position: "sticky", top: 0, zIndex: 2,
                          background: isWknd ? "#1a3366" : "#0f2744",
                          color: isWknd ? "#93c5fd" : "#e0e7ff",
                          fontWeight: 600, padding: "6px 4px", textAlign: "center",
                          minWidth: 56, maxWidth: 72,
                          borderRight: "1px solid #1e3a5f", lineHeight: 1.3,
                        }}
                      >
                        <div style={{ fontSize: 12 }}>{day}</div>
                        <div style={{ fontSize: 9, opacity: 0.8 }}>{month}</div>
                      </th>
                    );
                  })}

                  {["Total Hours", "Stages"].map((h) => (
                    <th
                      key={h}
                      style={{
                        position: "sticky", top: 0, zIndex: 2,
                        background: "#0a1f3d", color: "#fff", fontWeight: 700,
                        padding: "10px 10px", textAlign: "center", minWidth: 85,
                        borderLeft: "2px solid #1e3a5f", fontSize: 10,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {reportUsers.map((u, ri) => {
                  const rowBg = ri % 2 === 0 ? "#ffffff" : "#f8faff";
                  return (
                    <tr key={u.id}>
                      {/* Sticky name cell */}
                      <td
                        style={{
                          position: "sticky", left: 0, zIndex: 1,
                          background: rowBg, borderRight: "2px solid #e2e8f0",
                          padding: "8px 14px", minWidth: 170,
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
                          {u.firstName} {u.lastName}
                        </div>
                        <div style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 165 }}>
                          {u.email}
                        </div>
                      </td>

                      {/* Attendance cells */}
                      {dates.map((date) => {
                        const att = u.attendance[date] || { dayType: "WORKING", minutes: 0, stages: 0, dayLabel: null, isExpectedWorkingDay: false };
                        const cell = resolveCell(att);
                        return (
                          <td
                            key={date}
                            title={att.dayLabel ? `${att.dayType}: ${att.dayLabel}` : att.dayType}
                            style={{
                              textAlign: "center",
                              padding: "4px 2px",
                              background: cell.bg || rowBg,
                              borderRight: "1px solid #f0f4f8",
                              borderBottom: "1px solid #f0f4f8",
                              minWidth: 56,
                              verticalAlign: "middle",
                              opacity: cell.dim ? 0.75 : 1,
                            }}
                          >
                            <div style={{ fontWeight: cell.dim ? 400 : 700, color: cell.fg, fontSize: 11, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                              {cell.topLine}
                            </div>
                            {cell.bottomLine && (
                              <div style={{ fontSize: 9, color: cell.fg, opacity: 0.8, marginTop: 3, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                {cell.bottomLine}
                              </div>
                            )}
                          </td>
                        );
                      })}

                      {/* Summary cells */}
                      <td style={{ textAlign: "center", fontWeight: 700, color: "#1d4ed8", borderLeft: "2px solid #e2e8f0", background: rowBg, padding: "8px 10px", fontSize: 12 }}>
                        {formatDuration(u.summary.totalMinutes)}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: 700, color: "#7c3aed", background: rowBg, padding: "8px 10px", fontSize: 12 }}>
                        {u.summary.totalStages}
                      </td>
                    </tr>
                  );
                })}

                {/* Totals row — sticky at bottom of scroll container */}
                <tr>
                  <td
                    style={{
                      position: "sticky", left: 0, zIndex: 1,
                      background: "#1e3a5f", color: "#fff", fontWeight: 700,
                      padding: "8px 14px", fontSize: 11, borderRight: "2px solid #334d7a",
                    }}
                  >
                    Daily Total
                  </td>
                  {dates.map((date) => {
                    const tot = dateTotals[date];
                    const hasTot = tot.minutes > 0;
                    return (
                      <td
                        key={date}
                        style={{
                          textAlign: "center",
                          background: hasTot ? "#0f2744" : "#1a3a5f",
                          padding: "4px 2px",
                          borderRight: "1px solid #1e3a5f",
                          minWidth: 56,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {hasTot ? (
                          <>
                            <div style={{ fontWeight: 700, color: "#fff", fontSize: 10, lineHeight: 1.2 }}>
                              {fmtCell(tot.minutes)}
                            </div>
                            <div style={{ fontSize: 9, color: "#86efac", marginTop: 1 }}>
                              {tot.present}p
                            </div>
                          </>
                        ) : (
                          <span style={{ color: "#4b6a99", fontSize: 13 }}>—</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "center", background: "#0a1f3d", color: "#fff", fontWeight: 700, fontSize: 12, borderLeft: "2px solid #1e3a5f", padding: "8px 10px", whiteSpace: "nowrap" }}>
                    {formatDuration(reportUsers.reduce((s, u) => s + u.summary.totalMinutes, 0))}
                  </td>
                  <td style={{ textAlign: "center", background: "#0a1f3d", color: "#fff", fontWeight: 700, fontSize: 12, padding: "8px 10px", whiteSpace: "nowrap" }}>
                    {reportUsers.reduce((s, u) => s + u.summary.totalStages, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {reportUsers.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 14 }}>
              No activity logged for the selected users in this date range.
            </div>
          )}
        </div>
      )}

      {!report && !reportLoading && (
        <div
          style={{
            textAlign: "center", padding: "60px 0", color: "#9ca3af", fontSize: 14,
            background: "#f9fafb", borderRadius: 12, border: "1px dashed #e5e7eb",
          }}
        >
          Select users and a date range, then click <strong>Generate</strong> to view the attendance grid.
        </div>
      )}

      {reportLoading && (
        <div className="uar-loading" style={{ padding: "60px 0" }}>
          <div className="uar-spinner" />
          Generating report…
        </div>
      )}
    </div>
  );
}
